import { Component, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UntypedFormControl } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { PdfViewerComponent } from 'ng2-pdf-viewer';

import { GuideAndBucketContentService } from '../../services/guide-and-bucket-content.service';
import { CommonService } from '../../services/common.service';
import { DocumentGeneratorData } from 'src/app/services/reports.service';
import { TranslateService } from '@ngx-translate/core';

interface GuideViewerData {
  id?: number;
  title?: string;
  company?: string;
  content_region?: string;
  lang?: string;
  asset?: string;
  extension?: string;
  use?: string;
  external?: boolean;
  manager_only?: boolean;
  show_in_mis?: boolean;
  created_at?: string;

  // MVR . Report
  pin?: string;
  mvrReport?: string;
  firstName?: string;
  lastName?: string;

  // Company/Distributor FAQ Report
  category?: string;
}

interface DialogData {
  selectedRow: GuideViewerData;
  casualDialogType: string;
  fileToFetch?: string;
  documentGeneratorData?: DocumentGeneratorData;
}

@Component({
  selector: 'guide-viewer-dialog',
  templateUrl: './guide-viewer.component.html',
  styleUrls: ['./guide-viewer.component.scss'],
})
export class GuideViewerComponent {
  dialogTitle: string = '';
  titleDescription: string = '';
  showDetailsBufferProgress: boolean = false;
  streamSource: any;
  resourceReadyOrFailed: boolean = false;

  searchText = new UntypedFormControl();
  minDocSearchCharTrigger: number = 2;

  pdfDocumentReady: boolean = false;
  autoresizePdf: boolean = true;
  defaultPdfZoomValue: number = 1.1;
  pdfZoomValue: number;
  currentPdfPage: number = 1;
  totalPdfPages: number;

  fileExtension: string;

  @ViewChild(PdfViewerComponent) private pdfComponent: PdfViewerComponent;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private gBucketService: GuideAndBucketContentService,
    public cService: CommonService,
    public translate: TranslateService
  ) {
    this.initialize();
  }

  initialize() {
    this.pdfZoomValue = this.defaultPdfZoomValue;
    let title = '';
    let description = '';

    if (this.data.selectedRow.title) {
      title = this.data.selectedRow.title ? this.data.selectedRow.title : this.data.selectedRow.asset;
      description = this.data.selectedRow.asset ? this.data.selectedRow.asset : '';
    } else if (this.data.documentGeneratorData) {
      title = `${this.data.selectedRow.firstName} ${this.data.selectedRow.lastName} - ${this.data.selectedRow.pin}`;
      description = `${this.cService.readyTranslations.generic.generatedOn}: ${this.data.documentGeneratorData.generationDate}`;
    } else {
      // Assume MVR
      title = `MVR . ${this.data.selectedRow.firstName} ${this.data.selectedRow.lastName}`;
      description = this.data.selectedRow.pin;
    }

    this.fileExtension = this.data.selectedRow.extension || this.data.selectedRow.mvrReport;
    console.log('%c File Ext', 'color: limegreen', this.fileExtension);

    setTimeout(() => {
      this.dialogTitle = title;
      this.titleDescription = this.cService.toTitleCase(description);
    });

    console.log('%c Dialog Data', 'color: tomato', this.data);

    if (this.data.fileToFetch && this.data.fileToFetch === 'report-file') {
      if (this.data.documentGeneratorData) {
        this.getReportDocument(
          this.data.documentGeneratorData.endpoint,
          this.data.selectedRow.pin,
          this.fileExtension,
          this.data.documentGeneratorData.sendLanguage ? this.data.documentGeneratorData.language : null
        );
      } else {
        this.getReportDocument('mvr', this.data.selectedRow.pin, this.data.selectedRow.mvrReport);
      }
    }

    // Search input event
    this.searchText.valueChanges.pipe(debounceTime(500)).subscribe({
      next: (searchInput: string) => {
        const searchCommandParams = {
          caseSensitive: false,
          findPrevious: undefined,
          highlightAll: true,
          phraseSearch: true,
          query: searchInput,
        };

        if (searchInput && searchInput.split('').length >= this.minDocSearchCharTrigger) {
          this.pdfComponent.eventBus.dispatch('find', searchCommandParams);
        }
      },
    });
  }

  getReportDocument(endpoint: string, employeeNumber: string, fileExtension: string, language?: string) {
    this.showDetailsBufferProgress = true;

    this.gBucketService.getReportDocumentByColumnId(endpoint, employeeNumber, fileExtension, language).subscribe({
      next: (response: any) => {
        this.showDetailsBufferProgress = false;
        console.log('%c Requested Report File Stream Link', 'color: salmon', response);

        // Render PDF
        if (fileExtension.toLowerCase() === 'pdf') {
          setTimeout(() => {
            this.streamSource = response;
            this.resourceReadyOrFailed = true;
          });
        }
      },

      error: (error: any) => {
        this.showDetailsBufferProgress = false;
        this.resourceReadyOrFailed = true;
      },
    });
  }

  pdfDocumentLoaded(pdfData: { numPages: number }) {
    this.totalPdfPages = pdfData.numPages;
    this.pdfDocumentReady = true;
  }

  downloadFile() {
    window.open(this.streamSource, '_blank');
  }
}
